import * as React from 'react';
import { style } from 'typestyle';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { ContentClickedEventHandler } from '../../../common/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '../../../common/telemetryEvents/util/EventHandlerFactories';
import { telemetryConsumerWrapper } from '../../contexts/TelemetryConsumerWrapper';
import { ContentClickedTelemetryContext } from '../../contexts/TelemetryContexts';
import { ServiceLocator } from '../../ServiceLocator';
import { CardViewStyles } from './CardViewStyles';

export interface ThumbnailProps {
  readonly navigateUrl: string;
  readonly openLinkInNewTab?: boolean;
  readonly defaultImageUrl: string;
  readonly thumbnailImageUrl?: string;
  readonly imageCoverBackground?: boolean;
  readonly name?: string;
  readonly decorate?: boolean;
  readonly hoveredOrSelected?: boolean;
  readonly backgroundSize?: [number, number]; // X, Y px for background size (useful for SVGs)
  readonly useBase64EncodedImage?: boolean; // Use a base64 encoded svg data URI instead of an image
  readonly dontShowBorder?: boolean;
  readonly customHeight?: number;
  readonly allowFocusIndicator?: boolean;
}

export interface ThumbnailState {
  readonly loadThumbnailImage: boolean;
}

const CARD_STACK_DECORATION = <div className={CardViewStyles.ThumbnailWorkbookDecoration} />;

const focusIndicatorStyle: string = style({
  $nest: {
    '&:focus': CommonStyles.Focus.Indicator
  }
});

export class Thumbnail extends React.Component<ThumbnailProps, ThumbnailState> {
  private thumbnailImage: HTMLImageElement;
  private encodedSvg: string;

  constructor(props: ThumbnailProps) {
    super(props);
    this.state = { loadThumbnailImage: false };
    this.thumbnailImage = new Image();
  }

  public componentDidMount() {
    if (this.props.thumbnailImageUrl) {
      // If we are using a data uri, we need to fetch the content first
      if (this.props.useBase64EncodedImage) {
        const http = ServiceLocator.get<ng.IHttpService>('$http');
        http({
          method: 'GET',
          url: this.props.thumbnailImageUrl
        }).then((result: ng.IHttpResponse<string>) => {
          this.encodedSvg = result.data;
          this.setState({ loadThumbnailImage: true });
        });
      } else {
        this.thumbnailImage.src = this.props.thumbnailImageUrl;
        this.thumbnailImage.onload = () => {
          this.setState({ loadThumbnailImage: true });
        };
      }
    }
  }

  public componentWillUnmount() {
    this.thumbnailImage.onload = null;
  }

  public render() {
    const notPreloadedClassNames = this.props.dontShowBorder ? CardViewStyles.ThumbnailNoBorder : CardViewStyles.Thumbnail;
    const classNames = this.state.loadThumbnailImage ? [CardViewStyles.ThumbnailPreloaded] : [notPreloadedClassNames];
    const imageUri = this.props.useBase64EncodedImage ? `data:image/svg+xml;base64,${this.encodedSvg}` : this.props.thumbnailImageUrl;
    const backgroundImageUrl = this.state.loadThumbnailImage ? imageUri : this.props.defaultImageUrl;

    if (this.props.hoveredOrSelected) {
      classNames.push(CardViewStyles.CardHighlight);
    }
    if (this.props.allowFocusIndicator) {
      classNames.push(focusIndicatorStyle);
    }

    const loadedImageOrCoverBackground = this.props.imageCoverBackground || this.state.loadThumbnailImage;

    const thumbnailStyle = {
      backgroundImage: `url(${backgroundImageUrl})`,
      backgroundSize: this.props.backgroundSize ? `${this.props.backgroundSize[0]}px ${this.props.backgroundSize[1]}px` : loadedImageOrCoverBackground ? 'cover' : '',
      height: this.props.customHeight ? this.props.customHeight + 'px' : null
    };

    return telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) =>
      <div>
        <a href={this.props.navigateUrl}
          target={this.props.openLinkInNewTab ? '_blank' : null}
          onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
          className={classNames.join(' ')}
          style={thumbnailStyle}
          title={this.props.name}>
          <div data-tb-test-id={'thumbnail-overlay'}
            className={CardViewStyles.SelectOverlay}
            style={this.props.customHeight ? { height: this.props.customHeight + 'px' } : null}></div>
        </a>
        {this.props.decorate && CARD_STACK_DECORATION}
      </div>
    );
  }
}
